import { FormGroup, Input, Text } from '@southfields-digital/mxmz-react-ui';

import { getRepeaterItems, getValues } from 'src/components/GraphicPropertiesForm';
import Repeater from 'src/components/Repeater';
import type { Items, Id } from 'src/components/Repeater/types';

import styles from './DataPreview.module.scss';

type DataPreviewProps = {
  graphic: Graphic;
  previewData: PropertyValue;
  onUpdateProperty: (propertyId: string, value: string | PropertyValue[]) => void;
};

export default function DataPreview({ graphic, previewData, onUpdateProperty }: DataPreviewProps) {
  const repeaterProperties = graphic.properties.filter(
    (p): p is RepeaterProperties => p.type === 'repeater' && p.dataBinding !== undefined
  );

  const scalarProperties = graphic.properties.filter(
    (p) => p.type !== 'repeater' && p.dataBinding !== undefined
  );

  const hasRepeaters = repeaterProperties.length > 0;
  const hasScalars = scalarProperties.length > 0;

  if (!hasRepeaters && !hasScalars) {
    return (
      <div className={styles.Container}>
        <Text as="p" className={styles.EmptyState}>
          No data bindings configured for this graphic.
        </Text>
      </div>
    );
  }

  const handleScalarChange = (propertyId: string, value: string) => {
    onUpdateProperty(propertyId, value);
  };

  const handleRepeaterChange = (propertyId: string, _id: Id, _rowIndex: number, items: Items[]) => {
    const propertyValues: PropertyValue[] = getValues(items);
    onUpdateProperty(propertyId, propertyValues);
  };

  return (
    <div className={styles.Container}>
      {hasScalars && (
        <div className={styles.Section}>
          <div className={styles.ScalarGrid}>
            {scalarProperties.map((prop) => {
              const value = previewData[prop.id];
              const displayValue = value !== undefined && value !== null ? String(value) : '';

              return (
                <FormGroup key={prop.id} htmlFor={`preview-${prop.id}`} label={prop.name}>
                  <Input
                    id={`preview-${prop.id}`}
                    value={displayValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleScalarChange(prop.id, e.currentTarget.value)
                    }
                  />
                </FormGroup>
              );
            })}
          </div>
        </div>
      )}

      {repeaterProperties.map((repeaterProp) => {
        const propertyValues = previewData[repeaterProp.id];

        if (!Array.isArray(propertyValues) || propertyValues.length === 0) {
          return (
            <div key={repeaterProp.id} className={styles.RepeaterSection}>
              <Text as="h4" className={styles.RepeaterHeader}>
                {repeaterProp.name}
              </Text>
              <Text as="p" className={styles.EmptyState}>
                No data
              </Text>
            </div>
          );
        }

        const items = getRepeaterItems(repeaterProp, propertyValues as PropertyValue[]);

        return (
          <div key={repeaterProp.id} className={styles.RepeaterSection}>
            <Text as="h4" className={styles.RepeaterHeader}>
              {repeaterProp.name} ({items.length} items)
            </Text>
            <Repeater
              id={repeaterProp.id}
              onChangeRows={(id, rowIndex, newItems) =>
                handleRepeaterChange(repeaterProp.id, id, rowIndex, newItems)
              }
              items={items}
            />
          </div>
        );
      })}
    </div>
  );
}
